import React, { useState, useEffect, useCallback } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import axios from "axios";
import useMapLoader from "../../../../../Hooks/useMapLoader";
import Stats from "./devices/main";

const containerStyleSmall = {
  width: "300px",
  height: "200px",
  margin: "10px",
};

const containerStyleFull = {
  width: "100%",
  height: "100vh",
};

const Monitoring = () => {
  const mapKey = process.env.REACT_APP_MAP_KEY;
  const isLoaded = useMapLoader(mapKey);

  // Device stats state
  const [deviceStats, setDeviceStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState(null);

  // Existing map states
  const [expandedMap, setExpandedMap] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Sample assets data
  const assets = [
    {
      id: "7500313618",
      serial_no: "SNL7500313618",
      vehicle_plate: "KCA123A",
      lat: -1.286389,
      lng: 36.817223,
      speed: 0,
      mileage: 0,
      gpsTime: "2020-08-12T01:56:16Z",
      recvTime: "2020-08-12T01:56:31.036Z",
    },
    // ... other assets
  ];

  // Fetch device stats from API
  const fetchDeviceStats = useCallback(async () => {
    try {
      const authToken = "3585535a-137c-499e-82ad-af9feff7d8e7";
      const endpoints = [
        "https://icloud.assetscontrols.com:3443/OpenApi/Monitor",
        "http://icloud.assetscontrols.com:8092/OpenApi/Monitor",
      ].filter(Boolean);

      if (!authToken) {
        throw new Error(
          "Authentication token missing in environment variables"
        );
      }

      if (endpoints.length === 0) {
        throw new Error("No monitor endpoints configured");
      }

      const payload = {
        FAction: "QueryAssetStatusData",
        FTokenID: authToken,
      };

      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          const response = await axios.post(endpoint, payload, {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Request-ID": crypto.randomUUID(),
            },
            timeout: 5000,
          });

          if (response.data?.Result === 200) {
            setDeviceStats({
              total: response.data.FObject.FTotalCount,
              online: response.data.FObject.FOnlineCount,
              offline: response.data.FObject.FOfflineCount,
              inactive: response.data.FObject.FInactiveCount,
              motion: response.data.FObject.FMotionCount,
              stationary: response.data.FObject.FStationaryCount,
              idling: response.data.FObject.FIdlingCount,
              expiring: response.data.FObject.FExpiringCount,
              expired: response.data.FObject.FExpiredCount,
              unlocked: response.data.FObject.FUnLockCount,
              locked: response.data.FObject.FLockCount,
              doorOpen: response.data.FObject.FDoorOpenCount,
              doorClosed: response.data.FObject.FDoorCloseCount,
              alarms: response.data.FObject.FAlarmCount,
              lowBattery: response.data.FObject.FLowBatteryCount,
            });
            setStatsError(null);
            return;
          }

          // Handle specific API errors
          const errorMappings = {
            102: "Invalid parameters",
            104: "Token error or expired",
            105: "System abnormality",
          };

          lastError = new Error(
            errorMappings[response.data?.Result] ||
              "Failed to fetch device stats"
          );
        } catch (err) {
          lastError = err;
          console.error(`Endpoint ${endpoint} failed:`, err.message);
        }
      }

      throw lastError || new Error("All monitor endpoints failed");
    } catch (error) {
      setStatsError(error.message);
      console.error("Device stats fetch error:", error);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    fetchDeviceStats();
    // Set up polling every 60 seconds
    const intervalId = setInterval(fetchDeviceStats, 60000);
    return () => clearInterval(intervalId);
  }, [fetchDeviceStats]);

  // Filter assets based on search query
  const filteredAssets = assets.filter(
    (asset) =>
      asset.serial_no.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.vehicle_plate.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleMarkerClick = (asset) => {
    setSelectedAsset(asset);
  };

  const closeFloatingCard = () => setSelectedAsset(null);
  const expandMap = (id) => setExpandedMap(id);
  const minimizeMap = () => setExpandedMap(null);

  if (!isLoaded) {
    return <div>Loading maps...</div>;
  }

  return (
    <div className="p-4">
      {/* Device Statistics Panel */}
      <div className="mb-6 p-4 bg-white rounded-lg shadow">
        <h2 className="text-xl font-bold mb-4">Device Status</h2>
        {loadingStats && (
          <div className="text-gray-500">Loading statistics...</div>
        )}
        {statsError && (
          <div className="text-red-500 mb-2">
            Error: {statsError}
            <button
              onClick={fetchDeviceStats}
              className="ml-2 text-blue-500 hover:text-blue-700"
            >
              Retry
            </button>
          </div>
        )}
        {deviceStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard title="Total Devices" value={deviceStats.total} />
            <StatCard title="Online" value={deviceStats.online} status="good" />
            <StatCard
              title="Offline"
              value={deviceStats.offline}
              status="bad"
            />
            <StatCard title="In Motion" value={deviceStats.motion} />
            <StatCard
              title="Alarms"
              value={deviceStats.alarms}
              status={deviceStats.alarms > 0 ? "bad" : ""}
            />
            <StatCard
              title="Low Battery"
              value={deviceStats.lowBattery}
              status={deviceStats.lowBattery > 0 ? "warning" : ""}
            />
          </div>
        )}
      </div>

      {/* Maps Display */}
      {/* <div className="flex flex-wrap">
        {expandedMap
          ? // Fullscreen map view
            assets
              .filter((asset) => asset.id === expandedMap)
              .map((asset) => (
                <div key={asset.id} className="relative w-full">
                  <GoogleMap
                    mapContainerStyle={containerStyleFull}
                    center={{ lat: asset.lat, lng: asset.lng }}
                    zoom={15}
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                    }}
                  >
                    <Marker
                      position={{ lat: asset.lat, lng: asset.lng }}
                      onClick={() => handleMarkerClick(asset)}
                    />
                  </GoogleMap>
                  <button
                    onClick={minimizeMap}
                    className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded"
                  >
                    Minimize Map
                  </button>
                </div>
              ))
          : // Grid of small maps
            filteredAssets.map((asset) => (
              <div key={asset.id} className="relative m-2">
                <GoogleMap
                  mapContainerStyle={containerStyleSmall}
                  center={{ lat: asset.lat, lng: asset.lng }}
                  zoom={14}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                  }}
                >
                  <Marker
                    position={{ lat: asset.lat, lng: asset.lng }}
                    onClick={() => handleMarkerClick(asset)}
                  />
                </GoogleMap>
                <div className="text-center mt-2 text-sm">
                  <p>Serial No: {asset.serial_no}</p>
                  <p>Plate: {asset.vehicle_plate}</p>
                </div>
                <button
                  onClick={() => expandMap(asset.id)}
                  className="absolute bottom-2 right-2 bg-blue-500 text-white px-3 py-1 rounded-sm"
                >
                  Expand
                </button>
              </div>
            ))}
      </div> */}
      <div className="w-full px-4">
        <Stats />
      </div>
      {/* Asset Details Floating Card */}
      {selectedAsset && (
        <div className="fixed top-20 right-4 bg-white shadow-xl p-4 rounded-lg z-50 max-w-xs">
          <h3 className="font-bold text-lg mb-2">Asset Details</h3>
          <div className="space-y-2">
            <p>
              <span className="font-semibold">ID:</span> {selectedAsset.id}
            </p>
            <p>
              <span className="font-semibold">Serial No:</span>{" "}
              {selectedAsset.serial_no}
            </p>
            <p>
              <span className="font-semibold">Plate:</span>{" "}
              {selectedAsset.vehicle_plate}
            </p>
            <p>
              <span className="font-semibold">Speed:</span>{" "}
              {selectedAsset.speed} km/h
            </p>
            <p>
              <span className="font-semibold">Mileage:</span>{" "}
              {selectedAsset.mileage} km
            </p>
            <p>
              <span className="font-semibold">Last Update:</span>{" "}
              {new Date(selectedAsset.gpsTime).toLocaleString()}
            </p>
          </div>
          <button
            onClick={closeFloatingCard}
            className="mt-4 w-full bg-gray-500 text-white px-3 py-2 rounded"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

// StatCard component for displaying statistics
const StatCard = ({ title, value, status = "" }) => {
  const statusClasses = {
    good: "bg-green-100 text-green-800",
    bad: "bg-red-100 text-red-800",
    warning: "bg-yellow-100 text-yellow-800",
    default: "bg-blue-100 text-blue-800",
  };

  const className = statusClasses[status] || statusClasses.default;

  return (
    <div className={`p-3 rounded-lg ${className}`}>
      <h3 className="font-semibold text-sm">{title}</h3>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
};

export default React.memo(Monitoring);
